import classNames from "classnames";
import CurrencyInput from "components/CurrencyInput/CurrencyInput";
import Image from "next/image";
import { useState } from "react";
import { useWebTranslation, Trans } from "translations";
import { Icon } from "ui-kit";
import Colors from "ui-kit/colors";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { getCopays, getCurrencySymbol, getDeductibles } from "utils/site";
import { formatCurrency } from "utils/utils";
import Customiser from "../Customiser";
import Overlay from "../Overlay";
import styles from "./DeductibleCalculator.module.css";
import { useSignupStore } from "components/Signup/store/hooks";
import { trackSignupEvent } from "components/Signup/analytics";
import { StepName } from "components/Signup/steps/types";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";

export const DeductibleCalculatorBanner = () => {
  const { t } = useWebTranslation();
  const [showCalculator, setShowCalculator] = useState(false);

  const { getState } = useSignupStore();
  const { id } = useCurrentAnimal();

  const { copay, deductible, limit } = useCurrentInsuranceByAnimalId(id);

  return (
    <>
      <button
        className={styles.banner}
        onClick={() => {
          const state = getState();

          trackSignupEvent("DEDUCIBLE_CALCULATOR_OPEN" as StepName, 0, state);
          setShowCalculator(true);
        }}
      >
        <Image
          src="/images/illustrations/calculator.svg"
          width={40}
          height={40}
          alt=""
        />
        <Typography variant="body2">
          <strong>{t("deductible_calculator.banner")}</strong>
        </Typography>
        <div style={{ marginLeft: "auto" }}>
          <Icon name="chevron-right" size="S" color={Colors.grey400} />
        </div>
      </button>
      {showCalculator && (
        <DeductibleCalculator
          onClose={() => setShowCalculator(false)}
          copay={copay}
          deductible={deductible}
          limit={limit}
        />
      )}
    </>
  );
};

export const DeductibleCalculator = ({
  onClose,
  copay: currenctCopay,
  deductible: currentDeductible,
  limit: currentLimit,
}: {
  onClose: VoidFunction;
  copay: number;
  deductible: number;
  limit: number;
}) => {
  const { t } = useWebTranslation();

  const [cost, setCost] = useState(10000);
  const [limit, setLimit] = useState(currentLimit);
  const [deductible, setDeductible] = useState(currentDeductible);
  const [copay, setCopay] = useState(currenctCopay);

  const customerCost = Math.min(
    deductible / 100 + (cost - deductible / 100) * copay,
    cost
  );
  const lassieCost = Math.min(cost - customerCost, limit / 100);

  const notCovered = cost - lassieCost;

  return (
    <Overlay
      onRemove={onClose}
      display={{
        title: t("deductible_calculator.title"),
        content: (
          <>
            <div className={classNames(styles.box, styles.inputBox)}>
              <Typography variant="body2">
                <strong>{t("deductible_calculator.cost_label")}</strong>
              </Typography>
              <Gutter size={0.5} />
              <CurrencyInput
                value={cost === 0 ? "" : `${cost}`}
                onChangeText={(text: string) =>
                  setCost(text === "" ? 0 : parseInt(text, 10))
                }
                currencySymbol={getCurrencySymbol()}
              />
              <Gutter size={2} />
              <progress
                value={lassieCost}
                max={cost}
                className={styles.shareIndicator}
              />
              <Gutter size={2} />
              <div className={styles.paymentRow}>
                <div
                  className={styles.circle}
                  style={{ background: Colors.forrest400 }}
                />
                <Typography
                  variant="body2"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <strong>{t("deductible_calculator.you_pay")}</strong>
                  <strong>{formatCurrency(notCovered)}*</strong>
                </Typography>
              </div>
              <div className={styles.paymentRow}>
                <div
                  className={styles.circle}
                  style={{ background: Colors.blue200 }}
                />
                <Typography
                  variant="body2"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span>{t("deductible_calculator.lassie_pays")}</span>
                  <span>{formatCurrency(lassieCost)}</span>
                </Typography>
              </div>
            </div>
            <div className={classNames(styles.box, styles.selectorBox)}>
              <Typography variant="body2">
                <strong>{t("global.insurance_package")}</strong>
              </Typography>
              <Gutter size={2} />
              <Customiser
                options={[
                  { value: 30_000_00, title: t("plan_name.mini") },
                  { value: 60_000_00, title: t("plan_name.between") },
                  { value: 160_000_00, title: t("plan_name.big") },
                ]}
                onChange={(value) => setLimit(value)}
                value={limit}
                direction="row"
                slim
              />
              <Gutter size={3} />

              <Typography variant="body2">
                <strong>{t("global.fixed_deductible")}</strong>
              </Typography>
              <Gutter size={0} />
              <Typography variant="body3">
                {t("deductible_calculator.fixed_deductible.disclaimer")}
              </Typography>
              <Gutter size={2} />
              <Customiser
                options={getDeductibles().map((deductible) => ({
                  value: deductible,
                  title: formatCurrency(deductible / 100),
                }))}
                onChange={(value) => setDeductible(value)}
                value={deductible}
                direction="row"
                slim
              />
              <Gutter size={3} />

              <Typography variant="body2">
                <strong>{t("global.copay")}</strong>
              </Typography>
              <Gutter size={2} />
              <Customiser
                options={getCopays().map((copay) => ({
                  value: copay,
                  title: `${copay * 100}%`,
                }))}
                onChange={(value) => setCopay(value)}
                value={copay}
                direction="row"
                slim
              />
            </div>
            <div className={classNames(styles.box, styles.directPaymentBox)}>
              <div className={styles.icon}>
                <Icon name="money" size="M" color={Colors.blue300} />
              </div>
              <Typography variant="body2">
                <Trans
                  i18nKey="deductible_calculator.direct_payment"
                  components={[<strong key="0" />]}
                />
              </Typography>
            </div>
            <Gutter size={2} />
            <Typography variant="body4" align="center" component="div">
              {t("deductible_calculator.disclaimer")}
            </Typography>
          </>
        ),
      }}
    />
  );
};
